// ============================================================================
// Entity: PauseReasons
// Key: pauseReasons
// Kind: entity
// Generated file. Do not edit manually.
// ============================================================================

import {
	getPauseReasonsClientGetQueryKey,
	getPauseReasonsClientGetQueryOptions,
	getPauseReasonsDetailQueryKey,
	getPauseReasonsDetailQueryOptions,
	getPauseReasonsListQueryKey,
	getPauseReasonsListQueryOptions,
	getPauseReasonsRelationsTeamsAssignedQueryKey,
	getPauseReasonsRelationsTeamsAssignedQueryOptions,
	getPauseReasonsRelationsTeamsAvailableQueryKey,
	getPauseReasonsRelationsTeamsAvailableQueryOptions,
	getPauseReasonsRelationsUsersAssignedQueryKey,
	getPauseReasonsRelationsUsersAssignedQueryOptions,
	getPauseReasonsRelationsUsersAvailableQueryKey,
	getPauseReasonsRelationsUsersAvailableQueryOptions,
	getTeamsListQueryKey,
	getUsersListQueryKey,
	pauseReasonsCreate,
	pauseReasonsDelete,
	pauseReasonsRelationsTeamsAssign,
	pauseReasonsRelationsTeamsUnassign,
	pauseReasonsRelationsUsersAssign,
	pauseReasonsRelationsUsersUnassign,
	pauseReasonsUpdate,
} from '../api/backstage'
// #region Imports
import type {
	CreateOrUpdatePauseReasonRequest,
	PagedResultOfPauseReasonSummary,
	PagedResultOfTeamPauseReasonResponse,
	PagedResultOfTeamSummary,
	PagedResultOfUserDto,
	PagedResultOfUserPauseReasonDto,
	PauseReasonDetails,
	PauseReasonDto,
	PauseReasonSummary,
	TeamPauseReasonResponse,
	UserPauseReasonDto,
} from '../api/backstage.schemas'
import type { EntityConfig, ListQueryParams } from './entity-config.types'
import {
	defineMutationEndpoint,
	defineQueryEndpoint,
} from './entity-config.types'

// #endregion

// #region Entity Endpoints
const pauseReasonsEntityEndpoints = {
	create: defineMutationEndpoint<
		PauseReasonSummary,
		CreateOrUpdatePauseReasonRequest,
		undefined
	>({
		key: 'pauseReasons.create',
		method: 'POST',
		path: '/admin/pausereasons',
		mutationOptions: () => ({
			mutationFn: (variables: { data: CreateOrUpdatePauseReasonRequest }) =>
				pauseReasonsCreate(variables.data),
		}),
		invalidates: [(_context) => getPauseReasonsListQueryKey()],
	}),
	delete: defineMutationEndpoint<void, undefined, { pauseReasonId: number }>({
		key: 'pauseReasons.delete',
		method: 'DELETE',
		path: '/admin/pausereasons/{pauseReasonId}',
		pathParams: ['pauseReasonId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				pauseReasonId: number | string | null | undefined
			}) => pauseReasonsDelete(variables.pauseReasonId),
		}),
		invalidates: [(_context) => getPauseReasonsListQueryKey()],
	}),
	detail: defineQueryEndpoint<
		PauseReasonDetails,
		{ pauseReasonId: number },
		undefined
	>({
		key: 'pauseReasons.detail',
		method: 'GET',
		path: '/admin/pausereasons/{pauseReasonId}',
		pathParams: ['pauseReasonId'] as const,
		queryKey: (args) =>
			getPauseReasonsDetailQueryKey(args?.pathParams?.pauseReasonId),
		queryOptions: (args) =>
			getPauseReasonsDetailQueryOptions(args?.pathParams?.pauseReasonId),
	}),
	list: defineQueryEndpoint<
		PagedResultOfPauseReasonSummary,
		undefined,
		ListQueryParams
	>({
		key: 'pauseReasons.list',
		method: 'GET',
		path: '/admin/pausereasons',
		queryKey: (args) => getPauseReasonsListQueryKey(args?.queryParams),
		queryOptions: (args) => getPauseReasonsListQueryOptions(args?.queryParams),
	}),
	update: defineMutationEndpoint<
		PauseReasonSummary,
		CreateOrUpdatePauseReasonRequest,
		{ pauseReasonId: number }
	>({
		key: 'pauseReasons.update',
		method: 'PUT',
		path: '/admin/pausereasons/{pauseReasonId}',
		pathParams: ['pauseReasonId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				pauseReasonId: number | string | null | undefined
				data: CreateOrUpdatePauseReasonRequest
			}) => pauseReasonsUpdate(variables.pauseReasonId, variables.data),
		}),
		invalidates: [
			(_context) => getPauseReasonsListQueryKey(),
			(context) =>
				getPauseReasonsDetailQueryKey(context.entityId ?? context.ownerId),
		],
	}),
} as const
// #endregion

// #region Relations
// #region Relation: Teams
// Target Entity: teams
const pauseReasonsTeamsRelation = {
	key: 'teams',
	targetEntity: 'teams',
	endpoints: {
		assign: defineMutationEndpoint<
			TeamPauseReasonResponse[],
			(number | string)[],
			{ pauseReasonId: number }
		>({
			key: 'pauseReasons.relations.teams.assign',
			method: 'POST',
			path: '/admin/pausereasons/{pauseReasonId}/teams',
			pathParams: ['pauseReasonId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					pauseReasonId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					pauseReasonsRelationsTeamsAssign(
						variables.pauseReasonId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getPauseReasonsRelationsTeamsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getPauseReasonsRelationsTeamsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getPauseReasonsListQueryKey(),
				(_context) => getTeamsListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfTeamPauseReasonResponse,
			{ pauseReasonId: number },
			ListQueryParams
		>({
			key: 'pauseReasons.relations.teams.assigned',
			method: 'GET',
			path: '/admin/pausereasons/{pauseReasonId}/teams',
			pathParams: ['pauseReasonId'] as const,
			queryKey: (args) =>
				getPauseReasonsRelationsTeamsAssignedQueryKey(
					args?.pathParams?.pauseReasonId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getPauseReasonsRelationsTeamsAssignedQueryOptions(
					args?.pathParams?.pauseReasonId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfTeamSummary,
			{ pauseReasonId: number },
			ListQueryParams
		>({
			key: 'pauseReasons.relations.teams.available',
			method: 'GET',
			path: '/admin/pausereasons/{pauseReasonId}/assignable-teams',
			pathParams: ['pauseReasonId'] as const,
			queryKey: (args) =>
				getPauseReasonsRelationsTeamsAvailableQueryKey(
					args?.pathParams?.pauseReasonId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getPauseReasonsRelationsTeamsAvailableQueryOptions(
					args?.pathParams?.pauseReasonId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ pauseReasonId: number }
		>({
			key: 'pauseReasons.relations.teams.unassign',
			method: 'DELETE',
			path: '/admin/pausereasons/{pauseReasonId}/teams',
			pathParams: ['pauseReasonId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					pauseReasonId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					pauseReasonsRelationsTeamsUnassign(
						variables.pauseReasonId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getPauseReasonsRelationsTeamsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getPauseReasonsRelationsTeamsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getPauseReasonsListQueryKey(),
				(_context) => getTeamsListQueryKey(),
			],
		}),
	},
} as const
// #endregion

// #region Relation: Users
// Target Entity: users
const pauseReasonsUsersRelation = {
	key: 'users',
	targetEntity: 'users',
	endpoints: {
		assign: defineMutationEndpoint<
			UserPauseReasonDto[],
			(number | string)[],
			{ pauseReasonId: number }
		>({
			key: 'pauseReasons.relations.users.assign',
			method: 'POST',
			path: '/admin/pausereasons/{pauseReasonId}/users',
			pathParams: ['pauseReasonId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					pauseReasonId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					pauseReasonsRelationsUsersAssign(
						variables.pauseReasonId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getPauseReasonsRelationsUsersAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getPauseReasonsRelationsUsersAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getPauseReasonsListQueryKey(),
				(_context) => getUsersListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfUserPauseReasonDto,
			{ pauseReasonId: number },
			ListQueryParams
		>({
			key: 'pauseReasons.relations.users.assigned',
			method: 'GET',
			path: '/admin/pausereasons/{pauseReasonId}/users',
			pathParams: ['pauseReasonId'] as const,
			queryKey: (args) =>
				getPauseReasonsRelationsUsersAssignedQueryKey(
					args?.pathParams?.pauseReasonId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getPauseReasonsRelationsUsersAssignedQueryOptions(
					args?.pathParams?.pauseReasonId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfUserDto,
			{ pauseReasonId: number },
			ListQueryParams
		>({
			key: 'pauseReasons.relations.users.available',
			method: 'GET',
			path: '/admin/pausereasons/{pauseReasonId}/assignable-users',
			pathParams: ['pauseReasonId'] as const,
			queryKey: (args) =>
				getPauseReasonsRelationsUsersAvailableQueryKey(
					args?.pathParams?.pauseReasonId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getPauseReasonsRelationsUsersAvailableQueryOptions(
					args?.pathParams?.pauseReasonId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ pauseReasonId: number }
		>({
			key: 'pauseReasons.relations.users.unassign',
			method: 'DELETE',
			path: '/admin/pausereasons/{pauseReasonId}/users',
			pathParams: ['pauseReasonId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					pauseReasonId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					pauseReasonsRelationsUsersUnassign(
						variables.pauseReasonId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getPauseReasonsRelationsUsersAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getPauseReasonsRelationsUsersAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getPauseReasonsListQueryKey(),
				(_context) => getUsersListQueryKey(),
			],
		}),
	},
} as const
// #endregion
// #endregion

// #region Client Endpoints
const pauseReasonsClientEndpoints = {
	get: defineQueryEndpoint<PauseReasonDto[], undefined, undefined>({
		key: 'pauseReasons.client.get',
		method: 'GET',
		path: '/api/pausereasons',
		queryKey: (_args) => getPauseReasonsClientGetQueryKey(),
		queryOptions: (_args) => getPauseReasonsClientGetQueryOptions(),
	}),
} as const
// #endregion

// #region Entity Config Export
export const pauseReasonsEntityConfig = {
	key: 'pauseReasons',
	kind: 'entity',
	entity: {
		idField: 'id',
		idParam: 'pauseReasonId',
		routePath: '/pauseReasons',
	},
	endpoints: pauseReasonsEntityEndpoints,
	relations: {
		teams: pauseReasonsTeamsRelation,
		users: pauseReasonsUsersRelation,
	},
	client: pauseReasonsClientEndpoints,
} as const satisfies EntityConfig<'pauseReasons'>
// #endregion
